import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { UserCheck, Check, X, Loader2, Clock, Users, GraduationCap } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../components/PageHeader';
import { TableSkeleton } from '../../components/Skeletons';
import EmptyState from '../../components/EmptyState';
import UserAvatar from '../../components/UserAvatar';
import StatusPill from '../../components/StatusPill';
import { Button } from '../../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { CenterMorphModal, CenterMorphModalContent } from '../../components/beui/CenterMorphModal';
import { Textarea } from '../../components/ui/textarea';

export default function Pengguna() {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState([]);
  const [approved, setApproved] = useState([]);
  const [guruPending, setGuruPending] = useState([]);
  const [guruApproved, setGuruApproved] = useState([]);
  const [busy, setBusy] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [alasan, setAlasan] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const { data: links } = await supabase
      .from('parent_student_links')
      .select('*')
      .in('status', ['pending', 'lulus'])
      .order('created_at', { ascending: false });
    const parentIds = [...new Set((links || []).map((l) => l.parent_profile_id))];
    const studentIds = [...new Set((links || []).map((l) => l.student_id))];
    const [{ data: profiles }, { data: students }, { data: classes }] = await Promise.all([
      supabase.from('profiles').select('id, nama, telefon, aktif').in('id', parentIds.length ? parentIds : ['00000000-0000-0000-0000-000000000000']),
      supabase.from('students').select('id, nama, class_id').in('id', studentIds.length ? studentIds : ['00000000-0000-0000-0000-000000000000']),
      supabase.from('classes').select('id, nama'),
    ]);
    const pMap = Object.fromEntries((profiles || []).map((p) => [p.id, p]));
    const sMap = Object.fromEntries((students || []).map((s) => [s.id, s]));
    const cMap = Object.fromEntries((classes || []).map((c) => [c.id, c.nama]));
    const enrich = (l) => ({
      ...l,
      parent: pMap[l.parent_profile_id],
      studentNama: sMap[l.student_id]?.nama || '—',
      kelas: cMap[sMap[l.student_id]?.class_id] || '—',
    });
    setPending((links || []).filter((l) => l.status === 'pending').map(enrich));
    setApproved((links || []).filter((l) => l.status === 'lulus').map(enrich));

    // Permohonan guru (teacher_class_links)
    const { data: gLinks } = await supabase
      .from('teacher_class_links')
      .select('*')
      .in('status', ['pending', 'lulus'])
      .order('created_at', { ascending: false });
    const gProfileIds = [...new Set((gLinks || []).map((l) => l.profile_id))];
    const gClassIds = [...new Set((gLinks || []).map((l) => l.class_id))];
    const [{ data: gProfiles }, { data: gClasses }] = await Promise.all([
      supabase.from('profiles').select('id, nama, telefon, aktif').in('id', gProfileIds.length ? gProfileIds : ['00000000-0000-0000-0000-000000000000']),
      supabase.from('classes').select('id, nama').in('id', gClassIds.length ? gClassIds : ['00000000-0000-0000-0000-000000000000']),
    ]);
    const gpMap = Object.fromEntries((gProfiles || []).map((p) => [p.id, p]));
    const gcMap = Object.fromEntries((gClasses || []).map((c) => [c.id, c.nama]));
    const gEnrich = (l) => ({ ...l, guru: gpMap[l.profile_id], kelas: gcMap[l.class_id] || '—' });
    setGuruPending((gLinks || []).filter((l) => l.status === 'pending').map(gEnrich));
    setGuruApproved((gLinks || []).filter((l) => l.status === 'lulus').map(gEnrich));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (link) => {
    setBusy(link.id);
    const { error: e1 } = await supabase.from('parent_student_links')
      .update({ status: 'lulus', verified_by: profile.id }).eq('id', link.id);
    const { error: e2 } = await supabase.from('profiles')
      .update({ aktif: true }).eq('id', link.parent_profile_id);
    setBusy(null);
    if (e1 || e2) { toast.error('Gagal meluluskan. Coba lagi.'); return; }
    toast.success(`Permohonan ${link.parent?.nama || ''} diluluskan.`);
    load();
  };

  const approveGuru = async (link) => {
    setBusy(link.id);
    const { error: e1 } = await supabase.from('teacher_class_links')
      .update({ status: 'lulus', verified_by: profile.id }).eq('id', link.id);
    const { error: e2 } = await supabase.from('profiles')
      .update({ aktif: true }).eq('id', link.profile_id);
    setBusy(null);
    if (e1 || e2) { toast.error('Gagal meluluskan. Coba lagi.'); return; }
    toast.success(`Guru ${link.guru?.nama || ''} diluluskan.`);
    load();
  };

  const doReject = async () => {
    const { type, link } = rejectTarget;
    setBusy(link.id);
    const table = type === 'guru' ? 'teacher_class_links' : 'parent_student_links';
    const { error } = await supabase.from(table)
      .update({ status: 'tolak', alasan_tolak: alasan || null, verified_by: profile.id })
      .eq('id', link.id);
    setBusy(null); setRejectTarget(null); setAlasan('');
    if (error) { toast.error('Gagal menolak.'); return; }
    toast.success('Permohonan ditolak.');
    load();
  };

  const rejectName = rejectTarget?.type === 'guru' ? rejectTarget?.link?.guru?.nama : rejectTarget?.link?.parent?.nama;

  const Row = ({ l, showActions }) => (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 hover:bg-muted transition-colors" data-testid={`pengguna-row-${l.id}`}>
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <UserAvatar nama={l.parent?.nama} size={40} />
        <div className="min-w-0">
          <p className="font-semibold text-foreground truncate">{l.parent?.nama}</p>
          <p className="text-xs text-muted-foreground truncate">{l.parent?.telefon}</p>
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-muted-foreground">Anak: <span className="font-medium text-foreground">{l.studentNama}</span></p>
        <p className="text-xs text-muted-foreground">{l.kelas}</p>
      </div>
      {showActions ? (
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => approve(l)} disabled={busy === l.id}
                  className="h-10 bg-emerald2 hover:bg-emerald2-hover" data-testid={`approve-${l.id}`}>
            {busy === l.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-1" />{t('setuju')}</>}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setRejectTarget({ type: 'parent', link: l })}
                  className="h-10 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800/80 hover:bg-red-50 dark:hover:bg-red-950/60" data-testid={`reject-${l.id}`}>
            <X className="h-4 w-4 mr-1" />{t('tolak')}
          </Button>
        </div>
      ) : (
        <StatusPill status="lunas" />
      )}
    </div>
  );

  const GuruRow = ({ l, showActions }) => (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 hover:bg-muted transition-colors" data-testid={`pengguna-guru-row-${l.id}`}>
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <UserAvatar nama={l.guru?.nama} size={40} />
        <div className="min-w-0">
          <p className="font-semibold text-foreground truncate">{l.guru?.nama}</p>
          <p className="text-xs text-muted-foreground truncate">{l.guru?.telefon}</p>
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-muted-foreground">Kelas: <span className="font-medium text-foreground">{l.kelas}</span></p>
      </div>
      {showActions ? (
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => approveGuru(l)} disabled={busy === l.id}
                  className="h-10 bg-emerald2 hover:bg-emerald2-hover" data-testid={`approve-guru-${l.id}`}>
            {busy === l.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-1" />{t('setuju')}</>}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setRejectTarget({ type: 'guru', link: l })}
                  className="h-10 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800/80 hover:bg-red-50 dark:hover:bg-red-950/60" data-testid={`reject-guru-${l.id}`}>
            <X className="h-4 w-4 mr-1" />{t('tolak')}
          </Button>
        </div>
      ) : (
        <StatusPill status="lunas" />
      )}
    </div>
  );

  return (
    <div>
      <PageHeader
        title={t('pengguna')}
        subtitle={t('penggunaSub')}
        breadcrumbs={[{ label: t('pengurusan') }, { label: t('pengguna') }]}
      />

      <Tabs defaultValue="pending">
        <TabsList className="mb-4">
          <TabsTrigger value="pending" data-testid="tab-pending">
            <Clock className="h-4 w-4 mr-1.5" /><span className="sm:hidden">{t('tautPendek')}</span><span className="hidden sm:inline">{t('permohonanTaut')}</span>
            {pending.length > 0 && <span className="ml-2 h-5 min-w-5 px-1 rounded-full bg-amber-500 text-white text-xs flex items-center justify-center">{pending.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="approved" data-testid="tab-approved">
            <Users className="h-4 w-4 mr-1.5" />Diluluskan
          </TabsTrigger>
          <TabsTrigger value="guru" data-testid="tab-guru">
            <GraduationCap className="h-4 w-4 mr-1.5" /><span className="sm:hidden">{t('guruPendek')}</span><span className="hidden sm:inline">{t('permohonanGuru')}</span>
            {guruPending.length > 0 && <span className="ml-2 h-5 min-w-5 px-1 rounded-full bg-amber-500 text-white text-xs flex items-center justify-center">{guruPending.length}</span>}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending">
          {loading ? <TableSkeleton /> : pending.length === 0 ? (
            <EmptyState icon={UserCheck} title={t('tiadaPermohonan')} description="Semua permohonan orangtua telah diproses." testid="empty-pending" />
          ) : (
            <div className="rounded-2xl border border-border bg-card divide-y divide-border">
              {pending.map((l) => <Row key={l.id} l={l} showActions />)}
            </div>
          )}
        </TabsContent>

        <TabsContent value="approved">
          {loading ? <TableSkeleton /> : approved.length === 0 ? (
            <EmptyState icon={Users} title={t('belumTautanDiluluskan')} testid="empty-approved" />
          ) : (
            <div className="rounded-2xl border border-border bg-card divide-y divide-border">
              {approved.map((l) => <Row key={l.id} l={l} showActions={false} />)}
            </div>
          )}
        </TabsContent>

        <TabsContent value="guru">
          {loading ? <TableSkeleton /> : guruPending.length === 0 && guruApproved.length === 0 ? (
            <EmptyState icon={GraduationCap} title={t('tiadaPermohonanGuru')} description={t('tiadaPermohonanGuruDesc')} testid="empty-guru" />
          ) : (
            <div className="space-y-6">
              {guruPending.length > 0 && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">{t('permohonanTaut')} · {guruPending.length}</p>
                  <div className="rounded-2xl border border-border bg-card divide-y divide-border">
                    {guruPending.map((l) => <GuruRow key={l.id} l={l} showActions />)}
                  </div>
                </div>
              )}
              {guruApproved.length > 0 && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">{t('guruDiluluskan')} · {guruApproved.length}</p>
                  <div className="rounded-2xl border border-border bg-card divide-y divide-border">
                    {guruApproved.map((l) => <GuruRow key={l.id} l={l} showActions={false} />)}
                  </div>
                </div>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <CenterMorphModal open={!!rejectTarget} onOpenChange={(o) => !o && setRejectTarget(null)}>
        <CenterMorphModalContent ariaLabel={`${t('tolak')} permohonan`} className="bg-card" data-testid="reject-dialog">
          <div className="p-6">
            <div className="mb-4 pr-8">
              <h2 className="text-lg font-extrabold text-foreground">{t('tolak')} permohonan</h2>
            </div>
            <p className="text-sm text-muted-foreground">Nyatakan alasan penolakan untuk {rejectName}.</p>
            <Textarea value={alasan} onChange={(e) => setAlasan(e.target.value)} placeholder={t('alasanTolak')} data-testid="reject-reason" className="mt-3" />
            <div className="flex gap-2 mt-6">
              <Button variant="outline" onClick={() => setRejectTarget(null)} className="flex-1 h-11">{t('batal')}</Button>
              <Button className="flex-1 h-11 bg-red-600 hover:bg-red-700" onClick={doReject} data-testid="reject-confirm">
                {t('tolak')}
              </Button>
            </div>
          </div>
        </CenterMorphModalContent>
      </CenterMorphModal>
    </div>
  );
}
